import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { useConnectivity } from '../../contexts/ConnectivityContext'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import SyncIcon from '@mui/icons-material/Sync'

export function OfflineIndicator() {
  const { isOffline, isRecovering, connectivityState } = useConnectivity()

  const shouldShow = isOffline || isRecovering
  const [open, setOpen] = useState(false)
  const shownAtRef = useRef(null)

  useEffect(() => {
    const minDisplayMs = 900

    if (shouldShow) {
      if (open) return

      const isStartupRecovering = Boolean(isRecovering && connectivityState?.reason === 'startup')
      const delayMs = isOffline ? 0 : (isStartupRecovering ? 800 : 250)

      const showTimer = setTimeout(() => {
        setOpen(true)
        shownAtRef.current = Date.now()
      }, delayMs)

      return () => clearTimeout(showTimer)
    }

    if (!open) return

    const elapsed = Date.now() - (shownAtRef.current || 0)
    const remaining = Math.max(0, minDisplayMs - elapsed)
    const hideTimer = setTimeout(() => {
      setOpen(false)
    }, remaining)

    return () => clearTimeout(hideTimer)
  }, [shouldShow, isOffline, isRecovering, connectivityState?.reason, open])

  if (!open) {
    return null
  }

  const getIcon = () => {
    if (isRecovering) return <SyncIcon sx={{ animation: 'spin 2s linear infinite' }} />
    return <CloudOffIcon />
  }

  const getMessage = () => {
    if (isRecovering) return 'Reconnecting...'
    return 'Offline / Local Mode'
  }

  const getSubMessage = () => {
    if (isRecovering) return 'Restoring cloud connection'
    return 'Limited features available - Local network only'
  }

  return (
    <Box
      sx={{
        backgroundColor: isRecovering ? '#ff9800' : '#f57c00',
        color: 'white',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        zIndex: 1300,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        pointerEvents: 'none',
      }}
    >
      {getIcon()}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {getMessage()}
        </Typography>
        <Typography variant="caption">
          {getSubMessage()}
        </Typography>
      </Box>
      {connectivityState.reason && (
        <Chip
          label={connectivityState.reason}
          size="small"
          sx={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: '0.7rem',
          }}
        />
      )}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  )
}

export function OfflineIndicatorCompact() {
  const { isOffline, isRecovering } = useConnectivity()

  if (!isOffline && !isRecovering) {
    return null
  }

  return (
    <Chip
      icon={isRecovering ? <SyncIcon /> : <WifiOffIcon />}
      label={isRecovering ? 'Reconnecting' : 'Offline'}
      size="small"
      color={isRecovering ? 'warning' : 'error'}
      sx={{ ml: 1 }}
    />
  )
}
